import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import { getAllDiscountCodes } from "../../../../redux/reducers/clinicSlice";
import { getBillingCollectionsReportData } from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { PaymentTypeList } from "../../../../util/dropList";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./BillingCollections.css";

const tableHdrs = [
  "invoiceNumber",
  "invoiceDate",
  "paymentDate",
  "amtPaid",
  "paymentMode",
  "totalDue",
  "balanceDue",
  "discount",
  "discountCode",
  "taxes",
  "customerUserName",
  "petName",
];

const initSearch = {
  invoiceNumber: "",
  paymentMode: [],
  userName: "",
  petName: "",
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};
const filterByList = [
  "Invoice Date",
  "Payment Date",
  "Amount Paid",
  "Total Due",
  "Balance Due",
  "Discount",
  "Taxes",
];
const dropFields = ["Discount Code"];

const BillingCollectionsTableReport = () => {
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState("2025-08 to 2026-08");
  const [selectedPaymentModeFilter, setSelectedPaymentModeFilter] = useState("all");
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { discountCodes } = useSelector((state) => state?.clinic || {});
  const { billingCollections } = useSelector((state) => state?.report || {});
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getAllDiscountCodes());
  }, [dispatch]);

  useEffect(() => {
    onApiCall(1);
  }, [
    dateRange,
    selectedPaymentModeFilter,
    searchValues?.invoiceNumber,
    searchValues?.paymentMode,
    searchValues?.userName,
    searchValues?.petName,
  ]);

  const collectionsPayload = billingCollections?.overview || billingCollections?.data || {};
  const cards = collectionsPayload?.cards || {};

  const summaryCards = [
    {
      title: "Total outstanding",
      value:
        cards?.totalOutstandingDisplay ??
        (cards?.totalOutstanding !== undefined ? `₹${(cards.totalOutstanding / 100000).toFixed(1)}L` : "₹60.5L"),
    },
    {
      title: "Collection rate (week)",
      value:
        cards?.collectionRateWeekDisplay ??
        (cards?.collectionRateWeek !== undefined ? `${cards.collectionRateWeek}%` : "98.8%"),
    },
    {
      title: "Avg DSO (month)",
      value:
        cards?.avgDsoMonthDisplay ??
        (cards?.avgDsoMonth !== undefined ? `${cards.avgDsoMonth} days` : "25.2 days"),
    },
    {
      title: "Avg DSO (year)",
      value:
        cards?.avgDsoYearDisplay ??
        (cards?.avgDsoYear !== undefined ? `${cards.avgDsoYear} days` : "271.1 days"),
    },
  ];

  // Chart 1: Outstanding & aging (Bar Chart)
  const rawAging = Array.isArray(collectionsPayload?.outstandingAging)
    ? collectionsPayload.outstandingAging
    : [];

  const agingCategories =
    rawAging.length > 0
      ? rawAging.map((item) => item?.range || "Age")
      : ["0-30 days", "31-60 days", "61-90 days", "90+ days"];

  const agingValues =
    rawAging.length > 0
      ? rawAging.map((item) => (typeof item?.totalValue === "number" && !isNaN(item.totalValue) ? item.totalValue : 0))
      : [857511, 563386, 603249, 5053326];

  const agingChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, columnWidth: "40%", borderRadius: 3, distributed: true } },
    colors: ["#0F172A", "#14B8A6", "#94A3B8", "#818CF8"],
    xaxis: {
      categories: agingCategories,
      labels: { style: { fontFamily: "Montserrat, sans-serif", fontSize: "11px", colors: "#616161" } },
    },
    yaxis: {
      labels: {
        formatter: (val) => val >= 1000000 ? `${(val / 1000000).toFixed(1)}M` : val >= 100000 ? `${(val / 100000).toFixed(1)}L` : `${Math.round(val / 1000)}k`,
        style: { fontFamily: "Montserrat, sans-serif", fontSize: "12px", colors: "#616161" },
      },
    },
    legend: { show: false },
    grid: { borderColor: "#e5e7eb", strokeDashArray: 4 },
  };

  const agingChartSeries = [
    { name: "Outstanding", data: agingValues },
  ];

  // Chart 2: Payment method (Donut Chart)
  const rawPaymentMethods = Array.isArray(collectionsPayload?.paymentMethods)
    ? collectionsPayload.paymentMethods
    : [];

  const paymentMethodLabels =
    rawPaymentMethods.length > 0
      ? rawPaymentMethods.map((item) => item?.paymentMode || "Other")
      : ["Cash", "UPI", "Advance Adjustment", "Credit Card"];

  const paymentMethodSeries =
    rawPaymentMethods.length > 0
      ? rawPaymentMethods.map((item) => (typeof item?.percentage === "number" && !isNaN(item.percentage) ? item.percentage : typeof item?.amount === "number" && !isNaN(item.amount) ? item.amount : 0))
      : [34.7, 14.6, 11.5, 10.5];

  const paymentMethodChartOptions = {
    chart: { type: "donut", toolbar: { show: false } },
    labels: paymentMethodLabels,
    colors: ["#0F172A", "#2563EB", "#60A5FA", "#CBD5E1", "#10B981", "#8B5CF6", "#F59E0B", "#EC4899", "#14B8A6"],
    plotOptions: { pie: { donut: { size: "60%" } } },
    dataLabels: { enabled: false },
    legend: { position: "bottom", horizontalAlign: "center", fontFamily: "Montserrat, sans-serif", fontSize: "12px", markers: { width: 10, height: 10, radius: 2 } },
  };

  const paymentMethodChartSeries = paymentMethodSeries;

  const handleExportExcel = () => {
    const tableData = billingCollections?.data ?? [];
    const exportData = tableData.map((row) => ({
      "Invoice No.": row?.invoiceNumber ?? "",
      "Invoice Date": row?.invoiceDate ? moment(new Date(row?.invoiceDate)).format("DD MMM YYYY") : "",
      "Payment Date": row?.paymentDate ? moment(new Date(row?.paymentDate)).format("DD MMM YYYY") : "",
      "Amount Paid": row?.amtPaid ?? 0,
      "Payment Mode": row?.paymentMode ?? "",
      "Total Due": row?.totalDue ?? 0,
      "Balance Due": row?.balanceDue ?? 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Collections Report");
    XLSX.writeFile(workbook, `Collections_Report_${moment().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportPDF = () => {
    window.print();
  };

  const getDatesFromRange = (rangeStr) => {
    if (!rangeStr) return { startDate: "2025-08-01", endDate: "2026-08-31" };
    const parts = rangeStr.split(" to ");
    if (parts.length === 2) {
      const start = moment(parts[0], "YYYY-MM").startOf("month").format("YYYY-MM-DD");
      const end = moment(parts[1], "YYYY-MM").endOf("month").format("YYYY-MM-DD");
      return { startDate: start, endDate: end };
    }
    return { startDate: "2025-08-01", endDate: "2026-08-31" };
  };

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const { startDate, endDate } = getDatesFromRange(dateRange);
    const reqObj = {
      page: currentPage,
      itemSize: rowsPerPage,
      startDate,
      endDate,
      paymentMode: selectedPaymentModeFilter,
    };
    if (reset) return reqObj;
    if (searchValues?.invoiceNumber?.length > 0) reqObj.invoiceNo = searchValues?.invoiceNumber;
    if (searchValues?.paymentMode?.length > 0) reqObj.paymentMode = searchValues?.paymentMode?.map((li) => li?.value);
    if (searchValues?.userName?.length > 0) reqObj.userName = searchValues?.userName;
    if (searchValues?.petName?.length > 0) reqObj.petName = searchValues?.petName;
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    dispatch(getBillingCollectionsReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find((tblHdr) => tblHdr?.name === pd);
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => setEditColModVsble(false);

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders?.filter((act) => act?.isSelected)?.map((act) => act?.name);
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((billingCollections?.totalRecords ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => onApiCall(selectedPage);

  const getTableData = () => {
    return (billingCollections?.data ?? [])?.map((li) => {
      const reqDiscountCodes = (
        Array.isArray(li?.dicountCode) ? li?.dicountCode : []
      )?.map((nl) => {
        const reqCode = discountCodes?.find(
          (dc) => (dc?.billId ?? "")?.toString() === nl,
        );
        if (reqCode) return reqCode?.discountCode ?? "";
        return "";
      });
      return {
        ...li,
        invoiceNumber: li?.invoiceNo ?? "",
        invoiceDate: li?.invoiceDate ? moment(new Date(li?.invoiceDate)).format("DD MMM YYYY") : "",
        paymentDate: li?.paymentDate ? moment(new Date(li?.paymentDate)).format("DD MMM YYYY") : "",
        amtPaid: Number(li?.amountPaid ?? 0)?.toFixed(2),
        paymentMode: li?.paymentMode ?? "",
        totalDue: Number(li?.totalDue ?? 0)?.toFixed(2),
        balanceDue: Number(li?.balanceDue ?? 0)?.toFixed(2),
        discount: Number(li?.dicount ?? 0)?.toFixed(2),
        discountCode: reqDiscountCodes,
        taxes: Number(li?.taxes ?? 0)?.toFixed(2),
        customerUserName: li?.userName ?? "",
        petName: li?.petName ?? "",
      };
    });
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({ ...searchValues, [name?.trim()?.replace(/,$/, "")]: value });
  };

  const onFilterChange = (name, value) => setFilterValues({ ...filterValues, [name]: value });

  const onReset = () => {
    flushSync(() => setSearchValues(initSearch));
    flushSync(() => setFilterValues({ ...initFilter, selectedList: [], values: {} }));
    onApiCall(1, true);
  };

  const getDropListByType = (typ) => {
    return typ === "Payment Mode" ? PaymentTypeList : [];
  };

  const onApplyFilter = () => {
    onApiCall();
    setFilterValues({ ...filterValues, visible: false });
  };

  const onCloseFilter = () => setFilterValues({ ...filterValues, visible: false });

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    setFilterValues({ ...filterValues, values: { ...filterValues?.values, [name?.trim()?.replace(/,$/, "")]: value } });
  };

  const onFilterAdd = (value) => setFilterValues({ ...filterValues, added: value });

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) reqList = reqList?.filter((item) => item !== name);
    else reqList.push(name);
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterReset = () => setFilterValues({ ...initFilter, visible: true, selectedList: [], values: {} });

  return (
    <div className="billing-collections-container">
      {/* Title */}
      <h2 className="billing-collections-title">Collections report</h2>

      {/* Top Filter Bar */}
      <div className="billing-collections-filter-bar">
        <Select
          size="small"
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="2025-08 to 2026-08">2025-08 to 2026-08</MenuItem>
          <MenuItem value="2026-01 to 2026-08">2026-01 to 2026-08</MenuItem>
        </Select>

        <Select
          size="small"
          value={selectedPaymentModeFilter}
          onChange={(e) => setSelectedPaymentModeFilter(e.target.value)}
          sx={{
            height: 36,
            fontSize: 13,
            fontFamily: "Montserrat, sans-serif",
            borderRadius: "6px",
            backgroundColor: "#fff",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
          }}
        >
          <MenuItem value="all">Payment mode</MenuItem>
          <MenuItem value="cash">Cash</MenuItem>
          <MenuItem value="upi">UPI</MenuItem>
          <MenuItem value="credit">Credit Card</MenuItem>
        </Select>
      </div>

      {/* Export Action Buttons */}
      <div className="billing-collections-export-group">
        <button type="button" className="billing-collections-export-btn" onClick={handleExportExcel}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export Excel
        </button>
        <button type="button" className="billing-collections-export-btn" onClick={handleExportPDF}>
          <DownloadIcon sx={{ fontSize: 16 }} /> Export PDF
        </button>
      </div>

      {/* Summary Cards Grid (4 Cards) */}
      <div className="billing-collections-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="billing-collections-card" key={idx}>
            <h4 className="billing-collections-card-title">{card.title}</h4>
            <div className="billing-collections-card-value-container">
              <h2 className="billing-collections-card-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* Side-by-side Grid: Outstanding aging Bar Chart + Payment method Donut Chart */}
      <div className="billing-collections-charts-grid">
        <div className="billing-collections-chart-card">
          <div className="billing-collections-chart-header">
            Outstanding & aging
          </div>
          <Chart
            options={agingChartOptions}
            series={agingChartSeries}
            type="bar"
            height={280}
          />
        </div>

        <div className="billing-collections-chart-card">
          <div className="billing-collections-chart-header">
            Payment method
          </div>
          <Chart
            options={paymentMethodChartOptions}
            series={paymentMethodChartSeries}
            type="donut"
            height={280}
          />
        </div>
      </div>

      {/* Drill-down Section */}
      <div className="billing-collections-drilldown-header">Drill-down — payments</div>

      {/* Filter Row */}
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomTextField
              value={searchValues?.invoiceNumber ?? ""}
              name="invoiceNumber"
              handleChange={onSearchChange}
              label="Invoice No."
              backgroundColor={AppColors.lightBlue2}
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomSelect
              list={PaymentTypeList}
              value={searchValues?.paymentMode ?? []}
              name="paymentMode"
              handleChange={onSearchChange}
              label="Payment Mode"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              value={searchValues?.userName ?? ""}
              name="userName"
              handleChange={onSearchChange}
              label="Customer Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
            />
          </div>
          <div className="w15Per ml20">
            <CustomTextField
              value={searchValues?.petName ?? ""}
              name="petName"
              handleChange={onSearchChange}
              label="Pet Name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0 ? `${filterValues?.selectedList?.length} ` : ""}Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {billingCollections?.totalRecords ?? 0}
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {Math.ceil((billingCollections?.totalRecords ?? 0) / rowsPerPage) > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      {/* Edit Columns Modal */}
      <CustomModal open={editColModVsble} onClose={onEditModClose} header="Columns" modal modalWidth={25}>
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <CustomButton text="Save" smallBtn onClick={handleSaveTableColumn} />
          </div>
        </div>
      </CustomModal>

      {/* Filter Modal */}
      <CustomModal open={filterValues?.visible} onClose={onCloseFilter} header="Filter" rightModal modalWidth={30}>
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className="gray38 fs12 txt-regular">Filter by:</Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <CustomCheckbox
                      checked={filterValues?.selectedList?.includes(li)}
                      onChange={() => onSelectFilter(li)}
                    />
                    <Typography className="txt-regular fs14 black mb5 mlMin15">{li}</Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor" onClick={() => onFilterAdd(false)}>
                    <AddIcon sx={{ color: AppColors.appPrimary, fontSize: 15 }} />
                    <Typography className="font-medium fs12 blue-color ml5">Add Filter</Typography>
                  </div>
                  {filterValues?.selectedList?.map((li) => (
                    <div className="mt20" key={li}>
                      {dropFields?.includes(li) ? (
                        <CustomSelect
                          list={getDropListByType(li)}
                          value={filterValues?.values?.[li] ?? []}
                          name={li}
                          handleChange={onFilterValuesChange}
                          label={li}
                          multiSelectSearchCheck
                        />
                      ) : (
                        <CustomTextField
                          placeholder={li}
                          fullWidth
                          name={li}
                          handleChange={onFilterValuesChange}
                          value={filterValues?.values?.[li] ?? ""}
                          label={li}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <CustomButton text="Add" smallBtn onClick={() => onFilterAdd(true)} />
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <CustomButton text="Reset All" smallBtn onClick={onFilterReset} tabSelectdBtn />
                  <div className="ml20">
                    <CustomButton text="Apply Filters" smallBtn onClick={onApplyFilter} />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </div>
  );
};

export default BillingCollectionsTableReport;
